import { useState, useEffect, useCallback } from 'react';

/**
 * Серверная капча (/api/captcha/request + verify) для любого экрана.
 * captcha_issue_id связывает вопрос на экране с ответом в БД (гонка двух /request в Strict Mode).
 */
export function useServerCaptcha(apiUrl, sessionId, captchaEnabled) {
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaIssueId, setCaptchaIssueId] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  useEffect(() => {
    if (!captchaEnabled || !sessionId || captchaPassed || captchaQuestion) return;
    setCaptchaError('');
    let alive = true;
    fetch(`${apiUrl}/api/captcha/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setCaptchaError(data.error || data.message || `HTTP ${res.status}`);
          return;
        }
        if (data.success && data.question) {
          setCaptchaQuestion(data.question);
          setCaptchaIssueId(
            data.captcha_issue_id != null && data.captcha_issue_id !== ''
              ? Number(data.captcha_issue_id)
              : null,
          );
          return;
        }
        setCaptchaError(data.error || data.message || 'Could not load challenge');
      })
      .catch(() => {
        if (alive) setCaptchaError('Failed to load');
      });
    return () => {
      alive = false;
    };
  }, [captchaEnabled, sessionId, captchaPassed, captchaQuestion, apiUrl]);

  const handleCaptchaVerify = useCallback(async () => {
    if (!sessionId || !captchaAnswer.trim()) return false;
    setCaptchaLoading(true);
    setCaptchaError('');
    try {
      const body = {
        session_id: sessionId,
        answer: captchaAnswer.trim(),
      };
      if (captchaIssueId != null && !Number.isNaN(captchaIssueId)) {
        body.captcha_issue_id = captchaIssueId;
      }
      const res = await fetch(`${apiUrl}/api/captcha/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setCaptchaPassed(true);
        setCaptchaQuestion('');
        setCaptchaAnswer('');
        setCaptchaIssueId(null);
        return true;
      }
      if (data.error === 'captcha_stale' && data.message) {
        setCaptchaError(data.message);
        return false;
      }
      setCaptchaError(data.message || data.error || 'Invalid');
      return false;
    } catch {
      setCaptchaError('Error');
      return false;
    } finally {
      setCaptchaLoading(false);
    }
  }, [apiUrl, sessionId, captchaAnswer, captchaIssueId]);

  const invalidateCaptcha = useCallback((message) => {
    setCaptchaPassed(false);
    setCaptchaQuestion('');
    setCaptchaAnswer('');
    setCaptchaIssueId(null);
    setCaptchaError(message || '');
  }, []);

  const requestNewQuestion = useCallback(() => {
    if (!sessionId || !captchaEnabled) return;
    setCaptchaQuestion('');
    setCaptchaAnswer('');
    setCaptchaIssueId(null);
    fetch(`${apiUrl}/api/captcha/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setCaptchaError(data.error || data.message || `HTTP ${res.status}`);
          return;
        }
        if (data.success && data.question) {
          setCaptchaQuestion(data.question);
          setCaptchaIssueId(
            data.captcha_issue_id != null && data.captcha_issue_id !== ''
              ? Number(data.captcha_issue_id)
              : null,
          );
          return;
        }
        setCaptchaError(data.error || data.message || 'Could not load challenge');
      })
      .catch(() => setCaptchaError('Failed to load'));
  }, [apiUrl, sessionId, captchaEnabled]);

  return {
    captchaPassed,
    captchaQuestion,
    captchaAnswer,
    setCaptchaAnswer,
    captchaError,
    captchaLoading,
    handleCaptchaVerify,
    invalidateCaptcha,
    requestNewQuestion,
  };
}
